import { isErrorWithMessage } from "@/models/helper/ErrorWithMessage";
import { UserFacingError } from "@/models/helper/UserFacingError";
import { IPIC } from "@/models/management/PIC";
import { IManagedRoom } from "@/models/management/Room";
import { IRole } from "@/models/management/Role";
import { IRoomLocation } from "@/models/room/RoomLocation";
import axios from "axios";

export default class ManagementService {
  static async verifyAccess(): Promise<boolean> {
    try {
      await axios.get(
        `${import.meta.env.VITE_API_URL}management/verify`,
        {
          withCredentials: true,
        },
      );

      return true;
    } catch (_error) {
      return false;
    }
  }

  static async getPICs(search?: string): Promise<IPIC[]> {
    try {
      const res = await axios.get(
        `${import.meta.env.VITE_API_URL}management/pics`,
        {
          params: { search },
          withCredentials: true,
        },
      );
      return res.data.data;
    } catch (error) {
      throw new UserFacingError({
        title: "Fetch error.",
        description: "Failed to fetch PIC list.",
        logMessage: isErrorWithMessage(error) ? error.message : "Unknown error",
      });
    }
  }

  static async deletePIC(id: number): Promise<void> {
    try {
      await axios.delete(
        `${import.meta.env.VITE_API_URL}management/pics/${id}`,
        {
          withCredentials: true,
        },
      );
    } catch (error) {
      throw new UserFacingError({
        title: "Delete error.",
        description: `Failed to remove PIC with id ${id}.`,
        logMessage: isErrorWithMessage(error) ? error.message : "Unknown error",
      });
    }
  }

  static async createPIC(payload: any): Promise<void> {
    try {
      await axios.post(
        `${import.meta.env.VITE_API_URL}management/pics`,
        payload,
        {
          withCredentials: true,
        },
      );
    } catch (error) {
      throw new UserFacingError({
        title: "Create error.",
        description: "Failed to add new PIC.",
        logMessage: isErrorWithMessage(error) ? error.message : "Unknown error",
      });
    }
  }

  static async updatePIC(id: number, payload: any): Promise<void> {
    try {
      await axios.put(
        `${import.meta.env.VITE_API_URL}management/pics/${id}`,
        payload,
        {
          withCredentials: true,
        },
      );
    } catch (error) {
      throw new UserFacingError({
        title: "Update error.",
        description: "Failed to update PIC details.",
        logMessage: isErrorWithMessage(error) ? error.message : "Unknown error",
      });
    }
  }

  static async getRoles(): Promise<IRole[]> {
    try {
      const res = await axios.get(
        `${import.meta.env.VITE_API_URL}management/roles`,
        {
          withCredentials: true,
        },
      );

      const rolesArray = res.data?.data?.data;

      return Array.isArray(rolesArray) ? rolesArray : [];
    } catch (error) {
      console.error("Error fetching roles:", error);
      return [];
    }
  }

  static async getRooms(params?: {
    search?: string;
    locationId?: string;
  }): Promise<IManagedRoom[]> {
    try {
      const res = await axios.get(
        `${import.meta.env.VITE_API_URL}management/rooms`,
        {
          params: {
            search: params?.search,
            location_id: params?.locationId,
          },
          withCredentials: true,
        },
      );

      return res.data.data;
    } catch (error) {
      throw new UserFacingError({
        title: "Fetch error.",
        description: "Failed to fetch room list.",
        logMessage: isErrorWithMessage(error) ? error.message : "Unknown error",
      });
    }
  }

  static async getRoomDetail(id: number): Promise<IManagedRoom> {
    try {
      const res = await axios.get(
        `${import.meta.env.VITE_API_URL}management/rooms/${id}`,
        {
          withCredentials: true,
        },
      );

      return res.data.data;
    } catch (error) {
      throw new UserFacingError({
        title: "Fetch error.",
        description: "Failed to fetch room detail.",
        logMessage: isErrorWithMessage(error) ? error.message : "Unknown error",
      });
    }
  }

  static async assignRoomPIC(roomId: number, userId: number): Promise<void> {
    try {
      await axios.post(
        `${import.meta.env.VITE_API_URL}management/rooms/${roomId}/pics`,
        { user_id: userId },
        { withCredentials: true },
      );
    } catch (error) {
      throw new UserFacingError({
        title: "Assign error.",
        description: "Failed to assign PIC to room.",
        logMessage: isErrorWithMessage(error) ? error.message : "Unknown error",
      });
    }
  }

  static async removeRoomPIC(roomId: number, userId: number): Promise<void> {
    try {
      await axios.delete(
        `${import.meta.env.VITE_API_URL}management/rooms/${roomId}/pics/${userId}`,
        { withCredentials: true },
      );
    } catch (error) {
      throw new UserFacingError({
        title: "Remove error.",
        description: "Failed to remove PIC from room.",
        logMessage: isErrorWithMessage(error) ? error.message : "Unknown error",
      });
    }
  }

  static async getRoomLocations(): Promise<IRoomLocation[]> {
    try {
      const res = await axios.get(
        `${import.meta.env.VITE_API_URL}room-location/borrowing`,
        {
          withCredentials: true,
        },
      );

      return res.data.data;
    } catch (error) {
      throw new UserFacingError({
        title: "Fetch error.",
        description: "Failed to fetch room locations.",
        logMessage: isErrorWithMessage(error) ? error.message : "Unknown error",
      });
    }
  }

  static async getPICRooms(initial: string): Promise<any[]> {
    try {
      const res = await axios.get(
        `${import.meta.env.VITE_API_URL}management/pics/${initial}/rooms`,
        {
          withCredentials: true,
        },
      );
      return res.data?.data?.data || [];
    } catch (error) {
      return [];
    }
  }
}
